import { Fragment, FC, ReactNode } from 'react'

import { css, styled } from 'styled-components'

type BarProps = {
  title?: string
  left?: ReactNode
  right?: ReactNode
  center?: ReactNode
  gap?: string
  fullWidth?: boolean
  className?: string
}

const Bar: FC<BarProps> & { Section: typeof Fragment } = ({
  title,
  left,
  right,
  center,
  gap = '12px',
  fullWidth = false,
  className,
}) => (
  <Main className={className}>
    <Left $fullWidth={fullWidth}>
      {title && <Title>{title}</Title>}
      {!!left && <LeftSection $gap={gap}>{left}</LeftSection>}
    </Left>

    {!!center && (
      <Center>
        <CenterSection>{center}</CenterSection>
      </Center>
    )}

    {!!right && (
      <Right>
        <RightSection $gap={gap}>{right}</RightSection>
      </Right>
    )}
  </Main>
)

Bar.Section = Fragment

export { Spacer, Fragment as Section }

export default Bar

const Main = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
`

const Left = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  ${(p) =>
    p.$fullWidth &&
    css`
      width: 100%;
    `}
`

const Right = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const Center = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const sectionStyles = css`
  display: flex;
  flex-basis: auto;
  flex-flow: row no-wrap;
  flex-grow: 1;
  flex-wrap: nowrap;
  align-items: center;
  width: auto;
`

const Title = styled.span`
  display: inline-block;
  flex-grow: 0;
  align-items: center;
  margin-right: 12px;
  color: ${(p) => p.theme.text};
  font-size: 18px;
`

const LeftSection = styled.div<{ $gap?: string }>`
  ${sectionStyles}
  justify-content: flex-start;

  & > * {
    margin-right: ${(p) => p.$gap};

    &:last-child {
      margin-right: none;
    }
  }
`
const RightSection = styled.div<{ $gap?: string }>`
  ${sectionStyles}
  justify-content: flex-end;

  & > * {
    margin-left: ${(p) => p.$gap};

    &:first-child {
      margin-left: none;
    }
  }
`

const CenterSection = styled.div<{ $gap?: string }>`
  ${sectionStyles}
  justify-content: center;

  & > * {
    margin-left: ${(p) => p.$gap};

    &:first-child {
      margin-left: none;
    }
  }
`

const Spacer = styled.span`
  display: inline-block;
  align-self: stretch;
  width: 16px;
`
